/// <reference path="../../../../WebUI/typings/tsd.d.ts" />

import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Dialogs from "../../Dialogs/MessageBox";
import { ControllerBase } from "./ControllerBase";
import { DevPortalAppBase } from "../../DevPortalAppBase";
import * as Rest from "../../Generated/RestClient";
import * as Views from "../Views/BotsPage";
import { HeaderKind } from "../Utility/HeaderKind";
import { resourceStrings, NavLinks } from "../../../../Intercom.ReactUI/Views/index";
import * as ImageUtils from "../../Utils/ImageUtils";
import { BotProgress } from "../Views/status/index";
import { ArmClient } from "../../Services/index";
import { BotUtils, RollbackEndpointVersionName, BotUtilsV2 } from "../../Utils/BotUtils";
import { FlightUtils } from '../../Utils/FlightUtils';
import { Resources } from "../../Resources/Intercom.Resources.DevPortal";
import { getAzureIbizaBotUrl } from "../../Utils/Links";
import { clientAppSettings } from "../../Models/ClientAppSettings";

const PAGE_SIZE = 40;

export class BotsController extends ControllerBase {
    private state: Views.BotsPageState;
    private component: React.Component<Views.BotsPageProps, Views.BotsPageState>;
    private armClient: ArmClient;

    constructor(app: DevPortalAppBase) {
        super(app);
        this.headerKind = HeaderKind.Jumbo;

        // if coming back to this page from migration, navigate to the bot id
        const migrationBotId = localStorage.getItem("migrationBotId");
        if (migrationBotId) {
            this.app.navigate("/bots", { id: migrationBotId });
        }
    }

    protected handleSearch(s: string) {
        const search = s.trim();
        if (search) {
            if (!this.state.loadingMoreBots) {
                this.state.bots = [];
                this.state.searchString = search;
                this.state.continuationToken = null;
            }
            this.component.setState(this.state);
            this.fetchMore();
        }
    }

    private get filterSetting(): string {
        const param = (this.params['c'] || '').trim();
        let channel = param;
        if (param.indexOf('_all_') === 0) {
            channel = param.substr(5);
        }
        if (clientAppSettings.channels.filter(x => x.Id === channel).length) {
            return param;
        }
        return null;
    }

    public render(params: SimpleMvc.QueryParams): void {

        super.render(params);
        this.updateTitle('My bots | ' + resourceStrings.botFrameworkExtended);

        this.state = {
            bots: [],
            searchString: params['s'],
            continuationToken: null
        };

        this.setSearchState(this.state.searchString, !!this.state.searchString);

        var props: Views.BotsPageProps = {
            channelFilter: this.filterSetting,
            controller: this,
            onNextPage: () => {
                if (!this.state.loadingMoreBots && this.state.hasMoreBots) {
                    this.fetchMore()
                }
            },
            createBot: () => {
                this.app.navigate('/bots/provision');
            },
            navigateToBot: (bot: Rest.BotDocument): void => {
                if (bot.Kind) {
                    // if the bot has been migrated, link to Azure, otherwise stay on dev portal
                    const link = getAzureIbizaBotUrl(bot.SubscriptionId, bot.ResourceGroupName, bot.Id);
                    const $anchor = $(`<a href="${link}" target="_blank" hidden></a>`).appendTo("body");
                    $anchor[0].click();
                    $anchor.remove();
                } else {
                    this.app.navigate("/bots", { id: bot.Id });
                }
            },
            deleteBot: (botId: string) => {
                var options: Dialogs.IMessageBoxOptions = {
                    okButtonLabel: Resources.deleteBotDialogDeleteButtonText,
                    cancelButtonLabel: Resources.cancelButtonText,
                    className: "danger"
                };

                var mb = new Dialogs.MessageBox(options);
                mb.header = Resources.deleteBotDialogTitleText;
                mb.message = Resources.deleteBotDialogBodyText;
                mb.showDialog().done(() => {
                    this.performDelete(botId);
                });
            }
        }

        var element = React.createElement(Views.BotsPage, props);
        this.component = ReactDOM.render<Views.BotsPageProps, Views.BotsPage>(element, this.pageContent);

        this.setActiveJumboActiveLink(NavLinks.mybots);
        this.initArmClient();

        this.fetchMore();
    }

    /** Refreshes the page */
    private refreshPage(): void {
        window.location.href = "/bots";
    }

    /** Initializes instance of ARM client */
    private initArmClient(): void {
        if (this.azureAccessToken) {
            this.armClient = new ArmClient(this.azureAccessToken);
        }
    }

    /** Shows an overlay with status about bot rollback, rolls back bot, and then refreshes bots list. */
    private performRollback(bot: Rest.BotService): void {
        this.statusBar = React.createElement(BotProgress, { progress: Resources.rollbackBotDialogStatusPreparing });

        // ABSv1 & ABSCode bots need to rollback to old web / function app
        let upgradeAppName = null;
        // get rollback endpoint
        const oldEndpoint = _.find(bot.Endpoints || [], x => x.Version === RollbackEndpointVersionName) || { Url: bot.Endpoint };
        const oldEndpointUrl = oldEndpoint.Url;

        if (!BotUtils.isSDKBot(bot)) {
            // ensure there is an ARM client before starting the rollback process for non-registration-only bots
            if (!this.armClient) {
                this.statusBar = null;
                var options: Dialogs.IMessageBoxOptions = {
                    hideCancelButton: true,
                    okButtonLabel: Resources.okButtonText,
                    okButtonClass: "primary"
                };
                var mb = new Dialogs.MessageBox(options);
                mb.header = Resources.errorMessageErrorOccurred;
                mb.message = Resources.rollbackBotDialogErrorNoAzureToken;
                mb.showDialog();
                return;
            }

            upgradeAppName = bot.ProvisionInfo.SiteName;

            const siteNameRegex = /^https:\/\/(.*)\.azurewebsites.net/;
            let oldSiteName: string;
            try {
                const matches = siteNameRegex.exec(oldEndpointUrl);
                oldSiteName = matches[1];
            } catch (e) {
                this.statusBar = null;
                var options: Dialogs.IMessageBoxOptions = {
                    hideCancelButton: true,
                    okButtonLabel: Resources.okButtonText,
                    okButtonClass: "primary"
                };
                var mb = new Dialogs.MessageBox(options);
                mb.header = Resources.errorMessageErrorOccurred;
                mb.message = Resources.rollbackBotDialogErrorNoEndpoint;
                mb.showDialog();
                return;
            }

            bot.ProvisionInfo.SiteName = oldSiteName;
        }

        bot.Endpoint = oldEndpointUrl;
        if (bot.Endpoints) {
            bot.Endpoints = bot.Endpoints.filter(x => x.Version !== RollbackEndpointVersionName);
        }

        // for all types of bots
        bot.Kind = "";
        bot.ServicePlan = null;
        bot.ProvisioningState = null;
        bot.MigratedDate = null;

        this.statusBar = React.createElement(BotProgress, { progress: Resources.rollbackBotDialogStatusReverting });
        Rest.BotManagerClient.update(bot, false)
            .done(updatedBot => {
                if (upgradeAppName) {
                    this.wipeUpgradeEvidence(updatedBot, upgradeAppName);
                } else {
                    this.statusBar = null;
                    this.showPostRollbackDialog(bot.Id);
                }
            })
            .fail(err => {
                this.statusBar = null;
                this.handleHttpResponseError(err);
            });
    }

    /** Removes the deployment and web app caused by the upgrade. */
    private wipeUpgradeEvidence(bot: Rest.BotService, appName: string): void {
        const subId = bot.ProvisionInfo.SubscriptionId;
        const rgName = bot.ProvisionInfo.ResourceGroupName;
        const deploymentId = bot.ProvisionInfo.Deployment.id;

        this.statusBar = React.createElement(BotProgress, { progress: Resources.rollbackBotDialogStatusDeletingApp });
        this.armClient.deleteWebApp(subId, rgName, appName)
            .then(() => {
                this.statusBar = React.createElement(BotProgress, { progress: Resources.rollbackBotDialogStatusDeletingDeployment });
                return this.armClient.deleteDeployment(deploymentId);
            })
            .done(() => {
                this.statusBar = null;
                this.showPostRollbackDialog(bot.Id);
            })
            .fail(err => {
                this.statusBar = null;
                this.handleHttpResponseError(err);
            });
    }

    /** Shows a post-rollback confirmation dialogue */
    private showPostRollbackDialog(botId: string): void {
        var options: Dialogs.IMessageBoxOptions = {
            okButtonLabel: Resources.okButtonText,
            className: "primary",
            hideCancelButton: true
        };

        var mb = new Dialogs.MessageBox(options);
        mb.header = Resources.rollbackBotDialogConfirmationTitleText;
        mb.message = `${botId} ${Resources.rollbackBotDialogConfirmationBodyText}`;
        mb.showDialog().done(() => this.refreshPage());
    }

    /** Shows an overlay with status message and deletes the bot, then refreshes bots list */
    private performDelete(botId: string): void {
        this.statusBar = React.createElement(BotProgress, { progress: Resources.deleteBotDialogStatusDeleting });

        Rest.BotManagerClient.delete(botId).done(() => {
            if (this.isLoaded()) {
                this.statusBar = null;
                Dialogs.MessageBox.show(Resources.deleteBotDialogStatusDeleted).done(() => {
                    this.refreshPage();
                });
            }
        }).fail(xhr => {
            this.statusBar = null;
            this.handleHttpResponseError(xhr);
        });
    }

    private fetchMore() {
        if (this.state.searchString) {
            this.searchMore();
        } else {
            this.listMore();
        }
    }

    private searchMore() {
        this.state.loadingMoreBots = true;
        Rest.BotManagerClient.searchBotsPage(this.state.searchString, PAGE_SIZE, this.state.continuationToken).done(botSearchResult => {
            this.state.bots = this.state.bots.concat(botSearchResult.Items);
            this.state.loadingMoreBots = false;
            this.state.hasMoreBots = botSearchResult.HasMore;
            this.state.continuationToken = botSearchResult.ContinuationToken;
            this.component.setState(this.state);
            this.initAgentIcons();
            if (this.state.bots.length == 0) {
                // turn on no bots prompt in the botspage
                $('.no-bots-prompt').show();
            }
        }).fail(xhr => this.handleHttpResponseError(xhr));
    }

    private listMore() {
        this.state.loadingMoreBots = true;
        Rest.BotManagerClient.getPage(PAGE_SIZE, this.state.continuationToken).done(botSearchResult => {
            this.state.bots = this.state.bots.concat(botSearchResult.Items);
            this.state.loadingMoreBots = false;
            this.state.hasMoreBots = botSearchResult.HasMore;
            this.state.continuationToken = botSearchResult.ContinuationToken;
            this.component.setState(this.state);
            this.initAgentIcons();
            if (this.state.bots.length === 0) {
                // user has no bots, prompt to create/register
                this.app.navigate('/bots/provision', { createFirstBot: 'true' });
            }
        }).fail(xhr => this.handleHttpResponseError(xhr));
    }

    // TODO: shouldn't this be moved to view instead of leaving in the controller?
    private initAgentIcons(): void {
        var $icons = $(this.pageContent).find(".agent-icon");
        for (var i = 0; i < $icons.length; i++) {
            var $icon = $icons.eq(i);
            var iconUrl = <any>$icon.data('icon');
            if (iconUrl) {
                $icon.empty();
                $icon.removeData('icon');
                ImageUtils.addImageToContainer($icon, iconUrl, ImageUtils.ImageDisplayMode.Fill);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./Client/v4/Controllers/BotsController.ts